/**
 * Isometrische Geometrie für die Dashboard-Diagramme.
 *
 * Reine Zahlenarbeit: Die Funktionen liefern Polygone mit Füllung, die
 * Komponenten in `components/dashboardCharts.tsx` zeichnen sie. Farben sind
 * beliebige CSS-Farben (auch `var(--primary)`); Licht und Schatten entstehen
 * über `color-mix`, damit Farbprofil und Hell/Dunkel ohne Umrechnung wirken.
 */

export type Poly = { points: string; fill: string; stroke?: string };
type Pt = [number, number];
export type Projector = (x: number, y: number, z?: number) => Pt;

const K = 0.866;
export const RX = 1.2247;
export const RY = 0.7071;

/** t > 0 hellt auf, t < 0 dunkelt ab (0 … 1). */
export const shade = (color: string, t: number) =>
  t >= 0
    ? `color-mix(in srgb, ${color} ${Math.round((1 - t) * 100)}%, white)`
    : `color-mix(in srgb, ${color} ${Math.round((1 + t) * 100)}%, black)`;

export const proj = (cx: number, cy: number): Projector => (x, y, z = 0) => [cx + (x - y) * K, cy + (x + y) * 0.5 - z];

const pts = (a: Pt[]) => a.map((p) => `${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');

/** (x,z)-Polygon entlang y extrudieren; nur die sichtbaren Flächen. */
export function extrude(P: Projector, poly: Pt[], y0: number, d: number, color: string): Poly[] {
  let area = 0;
  poly.forEach((p, i) => {
    const q = poly[(i + 1) % poly.length];
    area += p[0] * q[1] - q[0] * p[1];
  });
  const s = area > 0 ? 1 : -1;
  const out: Poly[] = [];
  poly.forEach((p, i) => {
    const q = poly[(i + 1) % poly.length];
    const dx = q[0] - p[0];
    const dz = q[1] - p[1];
    const len = Math.hypot(dx, dz) || 1;
    const nx = (s * dz) / len;
    const nz = (-s * dx) / len;
    if (nx + nz <= 0.001) return;
    out.push({
      points: pts([P(p[0], y0, p[1]), P(q[0], y0, q[1]), P(q[0], y0 + d, q[1]), P(p[0], y0 + d, p[1])]),
      fill: shade(color, 0.12 * nz - 0.32 * nx),
    });
  });
  out.push({ points: pts(poly.map((p) => P(p[0], y0 + d, p[1]))), fill: shade(color, -0.06) });
  return out;
}

export function arrowPolys(P: Projector, x: number, y: number, w: number, h: number, d: number, color: string): Poly[] {
  const head = w * 1.3;
  return extrude(
    P,
    [[x, 0], [x + w, 0], [x + w, h - head], [x + w * 1.5, h - head], [x + w / 2, h], [x - w * 0.5, h - head], [x, h - head]],
    y,
    d,
    color,
  );
}

export type RingSegment = { v: number; color: string };

/** Extrudierter Ring um den Ursprung der Projektion; `sweep` begrenzt auf einen Bogen. */
export function ringPolys(
  P: Projector,
  ro: number,
  ri: number,
  h: number,
  segs: RingSegment[],
  opts: { start?: number; sweep?: number; gap?: number } = {},
): Poly[] {
  const [cx, cy] = P(0, 0, 0);
  const gap = opts.gap ?? 0.015;
  const start = opts.start ?? -Math.PI / 2;
  const sweep = opts.sweep ?? Math.PI * 2;
  const total = segs.reduce((sum, g) => sum + g.v, 0) || 1;
  let a = start;
  const S = segs.map((g) => {
    const span = (g.v / total) * sweep;
    const seg = { ...g, a0: a + gap, a1: a + span - gap, mid: a + span / 2 };
    a += span;
    return seg;
  });
  const pt = (r: number, ang: number, z: number): Pt => [cx + RX * r * Math.cos(ang), cy + RY * r * Math.sin(ang) - z];
  const wall = (s: (typeof S)[number], r: number, front: boolean): Poly[] => {
    const N = Math.max(2, Math.ceil((s.a1 - s.a0) / 0.08));
    const out: Poly[] = [];
    let run: number[] = [];
    const flush = () => {
      if (run.length > 1) {
        const t = run.map((q) => pt(r, q, h));
        const b = run.map((q) => pt(r, q, 0)).reverse();
        out.push({ points: pts(t.concat(b)), fill: shade(s.color, -0.1 - 0.22 * Math.cos(run[run.length >> 1])) });
      }
      run = [];
    };
    for (let i = 0; i <= N; i++) {
      const ang = s.a0 + ((s.a1 - s.a0) * i) / N;
      if (Math.sin(ang) >= 0 === front) run.push(ang);
      else flush();
    }
    flush();
    return out;
  };
  const top = (s: (typeof S)[number]): Poly => {
    const N = Math.max(2, Math.ceil((s.a1 - s.a0) / 0.08));
    const outer: Pt[] = [];
    const inner: Pt[] = [];
    for (let i = 0; i <= N; i++) {
      const ang = s.a0 + ((s.a1 - s.a0) * i) / N;
      outer.push(pt(ro, ang, h));
      inner.push(pt(ri, ang, h));
    }
    return { points: pts(outer.concat(inner.reverse())), fill: shade(s.color, 0.14), stroke: shade(s.color, 0.3) };
  };
  const sorted = [...S].sort((p, q) => Math.sin(p.mid) - Math.sin(q.mid));
  const out: Poly[] = [];
  if (ri > 0) sorted.forEach((s) => out.push(...wall(s, ri, false)));
  sorted.forEach((s) => out.push(...wall(s, ro, true)));
  sorted.forEach((s) => out.push(top(s)));
  return out;
}

/** Kreisausschnitt als SVG-Pfad (2D). */
export function sectorPath(cx: number, cy: number, ro: number, ri: number, a0: number, a1: number): string {
  const large = a1 - a0 > Math.PI ? 1 : 0;
  const p = (r: number, a: number) => `${(cx + r * Math.cos(a)).toFixed(1)},${(cy + r * Math.sin(a)).toFixed(1)}`;
  return `M${p(ro, a0)} A${ro},${ro} 0 ${large} 1 ${p(ro, a1)} L${p(ri, a1)} A${ri},${ri} 0 ${large} 0 ${p(ri, a0)} Z`;
}

/** Weicher Linienzug (Bézier) durch gleichmäßig verteilte Werte. */
export function smoothLine(values: number[], width: number, height: number, max: number, pad = 6) {
  if (!values.length) return { line: '', area: '' };
  const p: Pt[] = values.map((v, i) => [
    pad + (i / Math.max(1, values.length - 1)) * (width - 2 * pad),
    height - pad - (v / max) * (height - 2 * pad),
  ]);
  let d = `M${p[0][0].toFixed(1)},${p[0][1].toFixed(1)}`;
  for (let i = 1; i < p.length; i++) {
    const mx = (p[i - 1][0] + p[i][0]) / 2;
    d += ` C${mx.toFixed(1)},${p[i - 1][1].toFixed(1)} ${mx.toFixed(1)},${p[i][1].toFixed(1)} ${p[i][0].toFixed(1)},${p[i][1].toFixed(1)}`;
  }
  const last = p[p.length - 1];
  return { line: d, area: `${d} L${last[0].toFixed(1)},${height} L${p[0][0].toFixed(1)},${height} Z` };
}
